import { useEffect, useState } from 'react';

import Tabs from '@/components/Tabs.jsx';
import DataTable from '@/components/DataTable.jsx';
import { api, ApiError } from '@/lib/api.js';
import { CaptainsTab, ChipsTab, SelectionsTab } from './league/leagueTabs.jsx';
import './LeaguePage.css';

const num = (key, label, title) => ({ key, label, title: title ?? label, numeric: true });
const nf = new Intl.NumberFormat('en-GB');

/*
 * The top-1,000 sample.
 *
 * "Top 1K Aggregate Stats" said what the data was drawn from but not what
 * you would find, and the page's most useful table — the fifty managers in
 * best recent form — was buried behind that phrasing. The heading leads
 * with it instead, and says plainly which parts cover all thousand.
 *
 * One endpoint backs the page. Captains, selections and chips reuse the
 * league components: the shapes are identical.
 */
const formColumns = [
	{ key: 'position', label: 'Rank', title: 'Rank by form', numeric: true, defaultSort: 'asc' },
	{
		key: 'Player',
		label: 'Manager',
		strong: true,
		render: (name, row) =>
			row.Link ? (
				<a href={row.Link} target="_blank" rel="noreferrer">
					{name}
				</a>
			) : (
				name
			),
	},
	{ ...num('OR', 'Overall rank'), render: (v) => (v ? nf.format(v) : '—') },
	num('custom_points', 'Form pts', 'Points over the last five gameweeks'),
	{ ...num('TP', 'Total pts', 'Total points') },
	{
		...num('Score', 'GW pts', 'Gameweek points, with the transfer cost in brackets'),
		render: (v, row) => `${v} (${row.TC ? -row.TC : 0})`,
	},
	{ key: 'Captain', label: 'Captain' },
	{ key: 'VC', label: 'Vice', title: 'Vice-captain', searchable: false },
	{ key: 'Chip', label: 'Active chip' },
	num('Bench', 'Bench', 'Points left on the bench'),
	{ ...num('TV', 'Value', 'Team value'), render: (v) => `£${v}` },
	{
		...num('or_ratio', 'Rank gain', 'How far their overall rank has climbed'),
		render: (v) => (v == null ? '—' : `${v}×`),
	},
];

export default function TopManagersPage() {
	const [data, setData] = useState(null);
	const [status, setStatus] = useState('loading');
	const [error, setError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		api
			.get('/api/league_stats/top1k')
			.then((payload) => {
				if (cancelled) return;
				const body = payload?.result ?? payload;
				if (!body?.form?.length) {
					setError('No data recorded for the top 1,000 yet.');
					setStatus('error');
					return;
				}
				setData(body);
				setStatus('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : 'Could not load the data.');
				setStatus('error');
			});
		return () => {
			cancelled = true;
		};
	}, []);

	if (status === 'loading') {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading the top 1,000…</span>
			</div>
		);
	}

	if (status === 'error') {
		return (
			<div className="notice notice--error" role="alert">
				<strong>Could not load the top 1,000.</strong>
				<span>{error}</span>
			</div>
		);
	}

	const tabs = [
		{
			id: 'form',
			label: 'In-form 50',
			render: () => (
				<DataTable
					columns={formColumns}
					rows={data.form}
					rowKey={(r) => r.Player}
					filterPlaceholder="Filter by manager, captain or chip"
					maxHeight="70vh"
				/>
			),
		},
		data.captains?.length && {
			id: 'captains',
			label: 'Captains / EO',
			render: () => <CaptainsTab captains={data.captains} />,
		},
		{
			id: 'selections',
			label: 'Team Selections',
			render: () => <SelectionsTab selection={data.selection} />,
		},
		data.chips?.length && {
			id: 'chips',
			label: 'Chips',
			render: () => <ChipsTab chips={data.chips} />,
		},
	].filter(Boolean);

	return (
		<div className="league-page">
			<header className="league-page__intro">
				<div>
					<h1>In-form managers</h1>
					<p className="league-page__lede">
						The 50 most in-form managers from the Top 1K. Other tabs cover the
						whole Top 1K.
					</p>
				</div>
			</header>

			<Tabs tabs={tabs} />
		</div>
	);
}
